import React, { useState } from 'react';
import { Upload, X, FolderOpen, User } from 'lucide-react';
import CustomSelect from './CustomSelect';
import { useFolders } from '../contexts/FoldersContext';
import { useClients } from '../contexts/ClientsContext';
import { useProjects, Project } from '../contexts/ProjectsContext';
import { supabase } from '../lib/supabase';
import { resumableUpload, shouldUseResumableUpload } from '../lib/resumableUpload';
import { extractMetadataFromPDF, BidflowMetadata } from '../lib/bidflowMetadata';

function CreateProject({ onCancel }: { onCancel: () => void }) {
  console.log('🔥🔥🔥 CreateProject component loaded! TUS upload code is active!');
  
  const { folders: folderObjects, addFolder } = useFolders();
  const { clients: clientObjects, addClient } = useClients();
  const { addProject, projects } = useProjects();

  const [formData, setFormData] = useState({
    projectName: '',
    projectFolderId: '00000000-0000-0000-0000-000000000001',
    projectDescription: '',
    clientId: '',
    bidDeadline: '',
  });

  const [uploadedBlueprints, setUploadedBlueprints] = useState<File[]>([]);
  const [uploadedSpecs, setUploadedSpecs] = useState<File[]>([]);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [showNewClientInput, setShowNewClientInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState<string | null>('00000000-0000-0000-0000-000000000001');
  const [newClientName, setNewClientName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Get folder and client names for dropdown
  const folderNames = folderObjects.map((f) => f.name);
  const clientNames = clientObjects.map((c) => c.name);

  const handleBlueprintChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      // Check for duplicates
      const existingNames = uploadedBlueprints.map(f => f.name.toLowerCase());
      const duplicates: string[] = [];
      const uniqueFiles = newFiles.filter(file => {
        if (existingNames.includes(file.name.toLowerCase())) {
          duplicates.push(file.name);
          return false;
        }
        return true;
      });
      
      if (duplicates.length > 0) {
        alert(`The following blueprint files already exist and were skipped:\n${duplicates.join('\n')}`);
      }
      
      setUploadedBlueprints(prev => [...prev, ...uniqueFiles]);
    }
  };

  const handleSpecChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      // Check for duplicates
      const existingNames = uploadedSpecs.map(f => f.name.toLowerCase());
      const duplicates: string[] = [];
      const uniqueFiles = newFiles.filter(file => {
        if (existingNames.includes(file.name.toLowerCase())) {
          duplicates.push(file.name);
          return false;
        }
        return true;
      });
      
      if (duplicates.length > 0) {
        alert(`The following spec files already exist and were skipped:\n${duplicates.join('\n')}`);
      }
      
      setUploadedSpecs(prev => [...prev, ...uniqueFiles]);
    }
  };

  const removeBlueprint = (index: number) => {
    setUploadedBlueprints(prev => prev.filter((_, i) => i !== index));
  };

  const removeSpec = (index: number) => {
    setUploadedSpecs(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddFolder = async () => {
    if (newFolderName.trim()) {
      const newFolderId = await addFolder(newFolderName.trim(), newFolderParentId);
      if (newFolderId) {
        setFormData({ ...formData, projectFolderId: newFolderId });
      }
      setShowNewFolderInput(false);
      setNewFolderName('');
      setNewFolderParentId('00000000-0000-0000-0000-000000000001');
    }
  };

  const handleAddClient = async () => {
    if (newClientName.trim()) {
      const newClientId = await addClient(newClientName.trim());
      if (newClientId) {
        setFormData({ ...formData, clientId: newClientId });
      }
      setShowNewClientInput(false);
      setNewClientName('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔥 DEBUG: Form submitted! Starting project creation...');
    console.log('🔥 DEBUG: Blueprints to upload:', uploadedBlueprints.length);
    console.log('🔥 DEBUG: Specs to upload:', uploadedSpecs.length);
    uploadedBlueprints.forEach((file, i) => {
      console.log(`   Blueprint ${i + 1}: ${file.name} - ${(file.size / 1024 / 1024).toFixed(2)} MB`);
    });

    // Check for duplicate project name in same folder
    const duplicateProject = projects.find(
      p => p.name.toLowerCase() === formData.projectName.trim().toLowerCase() &&
           p.folderId === formData.projectFolderId
    );

    if (duplicateProject) {
      const folderName = folderObjects.find(f => f.id === formData.projectFolderId)?.name || 'this folder';
      alert(`A project named "${formData.projectName}" already exists in ${folderName}. Please choose a different name.`);
      return;
    }

    setIsCreating(true);

    try {
      // Check uploaded PDFs for embedded bidflow annotation metadata
      let detectedMetadata: BidflowMetadata | null = null;
      for (const blueprintFile of uploadedBlueprints) {
        if (blueprintFile.type === 'application/pdf' || blueprintFile.name.toLowerCase().endsWith('.pdf')) {
          try {
            const fileBytes = await blueprintFile.arrayBuffer();
            const meta = await extractMetadataFromPDF(fileBytes);
            if (meta) {
              console.log(`📦 Found bidflow metadata in ${blueprintFile.name}: ${meta.projectKey.components.length} components, ${Object.values(meta.annotations).reduce((s, a) => s + a.length, 0)} annotations`);
              detectedMetadata = meta;
              break;
            }
          } catch (err) {
            console.warn(`Could not check ${blueprintFile.name} for metadata:`, err);
          }
        }
      }

      // Get the client name
      const client = clientObjects.find((c) => c.id === formData.clientId);
      
      // Upload blueprints to Supabase Storage in PARALLEL for speed
      // Use TUS resumable uploads for large files (>40 MB)
      const uploadedBlueprintData = [];
      if (supabase && uploadedBlueprints.length > 0) {
        const uploadPromises = uploadedBlueprints.map(async (file, i) => {
          console.log(`🔍 DEBUG: Starting upload for ${file.name}, size: ${file.size} bytes (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
          
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = `blueprints/${fileName}`;
          
          let success = false;
          
          const shouldUseResumable = shouldUseResumableUpload(file.size);
          console.log(`🔍 DEBUG: Should use resumable upload? ${shouldUseResumable} (threshold: 40 MB = ${40 * 1024 * 1024} bytes)`);
          
          if (shouldUseResumable) {
            // Use TUS resumable upload for large files
            console.log(`📤 Large file detected (${(file.size / 1024 / 1024).toFixed(1)} MB): ${file.name}`);
            console.log('   Using TUS resumable upload protocol...');
            
            const result = await resumableUpload({
              bucketName: 'bucket',
              filePath,
              file,
              onProgress: (progress) => {
                console.log(`   Progress: ${progress.percentage}%`);
              },
              onError: (error) => {
                console.error(`   Upload error: ${error.message}`);
              }
            });
            
            success = result.success;
          } else {
            // Standard upload for smaller files
            const { data, error } = await supabase.storage
              .from('bucket')
              .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
              });
            
            if (error) {
              console.error('Error uploading blueprint:', error);
            } else {
              success = true;
            }
          }

          if (success) {
            // Get public URL
            const { data: urlData } = supabase.storage
              .from('bucket')
              .getPublicUrl(filePath);

            console.log(`✅ Uploaded: ${file.name}`);
            
            return {
              id: `file-${Date.now()}-${i}`,
              name: file.name,
              size: file.size,
              type: file.type,
              storagePath: filePath,
              url: urlData.publicUrl,
            };
          }
          return null;
        });

        const results = await Promise.all(uploadPromises);
        const validFiles = results.filter((r): r is NonNullable<typeof r> => r !== null);
        uploadedBlueprintData.push(...validFiles);
      }

      // Upload specs to Supabase Storage (all files separately)
      // Use TUS resumable uploads for large files (>40 MB)
      const uploadedSpecData = [];
      if (supabase && uploadedSpecs.length > 0) {
        console.log(`📤 Uploading ${uploadedSpecs.length} specification file(s)...`);
        
        // Upload all spec files in parallel
        const uploadPromises = uploadedSpecs.map(async (file, i) => {
          console.log(`🔍 DEBUG: Starting upload for ${file.name}, size: ${file.size} bytes (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
          
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}_${i}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = `specs/${fileName}`;
          
          let success = false;
          
          const shouldUseResumable = shouldUseResumableUpload(file.size);
          console.log(`🔍 DEBUG: Should use resumable upload? ${shouldUseResumable} (threshold: 40 MB = ${40 * 1024 * 1024} bytes)`);
          
          if (shouldUseResumable) {
            // Use TUS resumable upload for large files
            console.log(`📤 Large spec file detected (${(file.size / 1024 / 1024).toFixed(1)} MB): ${file.name}`);
            console.log('   Using TUS resumable upload protocol...');
            
            const result = await resumableUpload({
              bucketName: 'bucket',
              filePath,
              file,
              onProgress: (progress) => {
                console.log(`   Progress: ${progress.percentage}%`);
              },
              onError: (error) => {
                console.error(`   Upload error: ${error.message}`);
              }
            });
            
            success = result.success;
          } else {
            // Standard upload for smaller files
            const { data, error } = await supabase.storage
              .from('bucket')
              .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
              });
            
            if (error) {
              console.error(`Error uploading ${file.name}:`, error);
            } else {
              success = true;
            }
          }

          if (success) {
            const { data: urlData } = supabase.storage
              .from('bucket')
              .getPublicUrl(filePath);

            console.log(`✅ Uploaded: ${file.name}`);

            return {
              id: `spec-${Date.now()}-${i}`,
              name: file.name,
              size: file.size,
              type: file.type,
              storagePath: filePath,
              url: urlData.publicUrl,
            };
          }
          return null;
        });

        const results = await Promise.all(uploadPromises);
        const validFiles = results.filter((r): r is NonNullable<typeof r> => r !== null);
        uploadedSpecData.push(...validFiles);
        
        console.log(`✅ Uploaded ${validFiles.length} specification file(s)`);
      }
    
    // Create new project
    const newProject: Project = {
      id: `project-${Date.now()}-${Math.random()}`,
      name: formData.projectName,
      folderId: formData.projectFolderId,
      clientId: formData.clientId,
      clientName: client?.name || '',
      description: formData.projectDescription,
      bidDeadline: formData.bidDeadline,
      files: uploadedBlueprintData.length > 0 ? uploadedBlueprintData : undefined,
      specs: uploadedSpecData.length > 0 ? uploadedSpecData : undefined,
      // Default values
      address: '',
      city: '',
      state: '',
      zipCode: '',
      projectType: 'commercial',
      status: 'estimating', // New projects start in estimating phase
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
      await addProject(newProject, detectedMetadata ?? undefined);
      if (detectedMetadata) {
        console.log('✅ Project created with pre-populated annotations from bidflow metadata');
      }
      // Normalize projects from Supabase before the grid renders
      window.dispatchEvent(new Event('refetch-projects'));
      // Perform a hard reload to ensure stable PDF state and normalized URLs
      window.location.replace('/projects');
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Error creating project. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Loading Overlay */}
      {isCreating && (
        <div className="modal-overlay">
          <div className="loading-modal">
            <div className="loading-spinner" />
            <p className="loading-text">Creating project...</p>
            <p className="loading-subtext">
              {uploadedSpecs.length > 0
                ? `Uploading ${uploadedSpecs.length} specification file(s)...` 
                : 'Please wait while we upload your files'}
            </p>
          </div>
        </div>
      )}
      
      <div className="dashboard-main-centered">
        {/* Header */}
        <div className="create-project-header">
          <h1 className="create-project-title">Add New Project</h1>
        </div>

        {/* Form */}
        <div className="create-project-card">
          <form onSubmit={handleSubmit}>
            {/* Project Name */}
            <div className="form-group">
              <label className="form-label">Project Name *</label>
              <input
                type="text"
                className="form-input-large"
                placeholder="Enter project name"
                value={formData.projectName}
                onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                required
              />
            </div>

            {/* Project Folder */}
            <div className="form-group">
              <label className="form-label">Project Folder *</label>
              {!showNewFolderInput ? (
                <CustomSelect
                  value={folderObjects.find((f) => f.id === formData.projectFolderId)?.name || 'All Projects'}
                  onChange={(value) => {
                    const folder = folderObjects.find((f) => f.name === value);
                    if (folder) {
                      setFormData({ ...formData, projectFolderId: folder.id });
                    }
                  }}
                  options={folderNames}
                  icon={<FolderOpen size={18} />}
                  onAddNew={() => setShowNewFolderInput(true)}
                  addNewLabel="+ Add New Folder"
                  required
                />
              ) : (
                <div className="new-folder-form">
                  <div className="input-with-cancel">
                    <input
                      type="text"
                      className="form-input-large"
                      placeholder="Enter new folder name"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '0.75rem', marginTop: '0.75rem' }}>
                    <label className="form-label-small">Parent Folder</label>
                    <CustomSelect
                      value={folderObjects.find((f) => f.id === newFolderParentId)?.name || 'All Projects'}
                      onChange={(value) => {
                        const folder = folderObjects.find((f) => f.name === value);
                        if (folder) {
                          setNewFolderParentId(folder.id);
                        }
                      }}
                      options={folderNames}
                      icon={<FolderOpen size={18} />}
                    />
                  </div>
                  <div className="inline-actions">
                    <button
                      type="button"
                      className="cancel-inline-btn"
                      onClick={() => {
                        setShowNewFolderInput(false);
                        setNewFolderName('');
                        setNewFolderParentId('all-projects');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary-small"
                      onClick={handleAddFolder}
                    >
                      Add Folder
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Client */}
            <div className="form-group">
              <label className="form-label">Client *</label>
              {!showNewClientInput ? (
                <CustomSelect
                  value={clientObjects.find((c) => c.id === formData.clientId)?.name || ''}
                  onChange={(value) => {
                    const client = clientObjects.find((c) => c.name === value);
                    if (client) {
                      setFormData({ ...formData, clientId: client.id });
                    }
                  }}
                  options={clientNames}
                  placeholder="Select a client"
                  icon={<User size={18} />}
                  onAddNew={() => setShowNewClientInput(true)}
                  addNewLabel="+ Add New Client"
                  required
                />
              ) : (
                <div className="new-client-form">
                  <div className="input-with-cancel">
                    <input
                      type="text"
                      className="form-input-large"
                      placeholder="Enter new client name"
                      value={newClientName}
                      onChange={(e) => setNewClientName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="inline-actions">
                    <button
                      type="button"
                      className="cancel-inline-btn"
                      onClick={() => {
                        setShowNewClientInput(false);
                        setNewClientName('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary-small"
                      onClick={handleAddClient}
                    >
                      Add Client
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Bid Deadline */}
            <div className="form-group">
              <label className="form-label">Bid Deadline *</label>
              <input
                type="date"
                className="form-input-large"
                value={formData.bidDeadline}
                onChange={(e) => setFormData({ ...formData, bidDeadline: e.target.value })}
                required
              />
            </div>

            {/* Project Description */}
            <div className="form-group">
              <label className="form-label">Project Description</label>
              <textarea
                className="form-textarea-large"
                placeholder="Enter project description"
                rows={5}
                value={formData.projectDescription}
                onChange={(e) => setFormData({ ...formData, projectDescription: e.target.value })}
              />
            </div>

            {/* Upload Blueprints */}
            <div className="form-group">
              <label className="form-label">Upload Blueprints *</label>
              <label className="file-upload-box">
                <input
                  type="file"
                  accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png"
                  onChange={handleBlueprintChange}
                  style={{ display: 'none' }}
                  multiple
                  required={uploadedBlueprints.length === 0}
                />
                <div className="upload-icon-wrapper">
                  <Upload size={28} />
                </div>
                <div className="upload-text-main">Click to upload blueprints</div>
                <div className="upload-text-sub">PDF, DWG, DXF, JPG, or PNG • Multiple files supported</div>
              </label>
              
              {uploadedBlueprints.length > 0 && (
                <div className="uploaded-files-list">
                  {uploadedBlueprints.map((file, index) => (
                    <div key={index} className="file-preview-card">
                      <div className="file-preview-left">
                        <div className="file-preview-icon">
                          <Upload size={20} />
                        </div>
                        <div className="file-preview-info">
                          <div className="file-preview-name">{file.name}</div>
                          <div className="file-preview-size">
                            {(file.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="file-remove-btn"
                        onClick={() => removeBlueprint(index)}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Specs */}
            <div className="form-group">
              <label className="form-label">Upload Specifications <span style={{ color: 'var(--text-placeholder)', fontWeight: 'normal', fontSize: '0.9rem' }}>(Optional)</span></label>
              <label className="file-upload-box">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleSpecChange}
                  style={{ display: 'none' }}
                  multiple
                />
                <div className="upload-icon-wrapper">
                  <Upload size={28} />
                </div>
                <div className="upload-text-main">Click to upload specifications</div>
                <div className="upload-text-sub">PDF, DOC, DOCX, or TXT • Optional • Can be added later</div>
              </label>
              
              {uploadedSpecs.length > 0 && (
                <div className="uploaded-files-list">
                  {uploadedSpecs.map((file, index) => (
                    <div key={index} className="file-preview-card">
                      <div className="file-preview-left">
                        <div className="file-preview-icon">
                          <Upload size={20} />
                        </div>
                        <div className="file-preview-info">
                          <div className="file-preview-name">{file.name}</div>
                          <div className="file-preview-size">
                            {(file.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="file-remove-btn"
                        onClick={() => removeSpec(index)}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="form-actions-centered">
              <button type="button" className="btn-cancel-large" onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="btn-create-large">
                Create Project
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateProject;
